import { z } from 'zod';
import {
  GIT_REPO_ATTACHMENT_ID,
  type GitRepoAttachmentConfig,
  GitRepoAttachmentConfigSchema,
  type PluginAttachmentValue,
  PluginAttachmentValueSchema,
  SandboxOptionsSchema,
  UserContentBlocksSchema,
} from '@runloop/reflex-contract';
import { AgentAuthMethodSchema } from './secret.types.js';
import { ModelProviderSecretIdSchema } from './model-provider-secrets.types.js';
import { AgentAssociationSchema } from './agent-association.types.js';
import { MAX_LAUNCH_LABELS } from './agent-label.types.js';
import { coreIds } from './ids.js';
import { BackgroundTaskSchema } from './event.types.js';

/**
 * One-shot state carried when another page navigates to the agent launcher.
 *
 * `openLaunchDialog` is used by host-level surfaces such as the command
 * palette. Plugin pages can also provide attachments that the launch dialog
 * should apply on top of the user's remembered launch configuration.
 */
export const LaunchAgentRouteStateSchema = z.object({
  openLaunchDialog: z.boolean().optional(),
  attachments: z.array(PluginAttachmentValueSchema).optional(),
});
export type LaunchAgentRouteState = z.infer<typeof LaunchAgentRouteStateSchema>;

/**
 * A user-defined environment variable to inject into the agent's devbox.
 *
 * Non-secret entries store the value inline (`value` set, `isSecret` false/undefined).
 * Secret entries are stored externally — the API caller may submit a one-time
 * inline `value` which the server replaces with a generated `secretRef` (the
 * Runloop secret name) before persisting. Persisted secret entries always have
 * `secretRef` set and `value` absent.
 */
export const AgentEnvVarSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Key is required')
      .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Key must be a valid env var name'),
    value: z.string().optional(),
    secretRef: z.string().optional(),
    isSecret: z.boolean().optional(),
  })
  .refine((v) => v.value !== undefined || v.secretRef !== undefined, {
    message: 'Either value or secretRef must be provided',
    path: ['value'],
  });
export type AgentEnvVar = z.infer<typeof AgentEnvVarSchema>;

/**
 * Launch-time env var input schema. Accepts two safe forms:
 *   - Inline value: `{ key, value, isSecret?: boolean }`
 *   - Selected org secret: `{ key, secretRef, isSecret: true }`
 *
 * Server code must validate that submitted `secretRef` values are either:
 *   - Generated refs (matching the `agents/<id>/env/...` pattern)
 *   - Existing org secrets (validated via SecretService.resolveSecret)
 */
export const AgentEnvVarInputSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Key is required')
      .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Key must be a valid env var name'),
    value: z.string().optional(),
    secretRef: z.string().optional(),
    isSecret: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.value !== undefined || v.secretRef !== undefined, {
    message: 'Either value or secretRef must be provided',
    path: ['value'],
  });
export type AgentEnvVarInput = z.infer<typeof AgentEnvVarInputSchema>;

export const AgentStatusSchema = z.enum([
  'starting',
  'running',
  'needs_input',
  'completed',
  'interrupted',
  'stopping',
  'stopped',
  'error',
  'terminated',
]);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

/** Latest ordinary-turn boundary durably accepted for an agent. */
export const AgentTurnStateSchema = z.enum(['working', 'idle']);
export type AgentTurnState = z.infer<typeof AgentTurnStateSchema>;

/**
 * A user-applied triage label on an agent run — what the *person* says the run
 * needs, not what the runner observed. Distinct from {@link AgentStatusSchema}
 * (the lifecycle the server derives from the run itself): a run can be
 * `interrupted` and marked `blocked`, or `completed` and marked `in_review`.
 *
 * Marks are only meaningful while the run is not working, and the server drops
 * one the moment the run starts another turn or is archived — see
 * `clearMarkOnTurnStart` / `setArchived` in the agent query service. That keeps
 * a mark a claim about the run's *current* resting state rather than a stale
 * label somebody forgot to remove.
 */
export const AgentMarkSchema = z.enum(['blocked', 'in_review', 'completed']);
export type AgentMark = z.infer<typeof AgentMarkSchema>;

/** Every mark, in the order surfaces should offer them. */
export const AGENT_MARKS: readonly AgentMark[] = AgentMarkSchema.options;

export const AgentTypeSchema = z.string();
export type AgentType = z.infer<typeof AgentTypeSchema>;

/**
 * Per-agent prompt mode controls how the agent interacts with the
 * working tree.
 *
 *   - `implement`: default autonomous mode — the agent executes fully.
 *   - `plan`: the agent produces a plan and waits for approval before
 *     making changes.
 *   - `review`: read-only analysis mode — the agent can read, search,
 *     and analyze code but cannot modify files. Write tools are denied
 *     at the provider level, not just by instruction.
 */
export const PromptModeSchema = z.enum(['implement', 'plan', 'review']);
export type PromptMode = z.infer<typeof PromptModeSchema>;

/**
 * Live state of one service on the agent's sandbox — a plugin-contributed
 * launch step or a runtime-registered daemon. Built on demand by sweeping the
 * on-box service state (`~/.reflex/services`): the persisted `<id>.status`
 * value plus a `kill -0` liveness probe of the recorded pid, so a daemon that
 * died after registering reports `running: false` instead of its last-seen
 * status. Returned by `GET /agents/:id/services/status`.
 */
export const AgentServiceStatusSchema = z
  .object({
    /** On-box service id — the key for `GET /agents/:id/services/:serviceId/logs`. */
    id: z.string(),
    /** Display label from the service definition; absent for boot-only steps. */
    label: z.string().optional(),
    /** Port from the service definition, when the service registered one. */
    port: z.number().int().positive().optional(),
    /** Last persisted lifecycle status (`ready`, `starting`, `error:*`, ...). */
    status: z.string(),
    /** Recorded process id; absent for one-shot steps that leave no pidfile. */
    pid: z.number().int().positive().optional(),
    /**
     * Whether the recorded pid is currently alive. Absent when the service
     * left no pidfile (one-shot launch commands), so absence means "not a
     * process", not "dead".
     */
    running: z.boolean().optional(),
    /**
     * `source` from the service definition: `agent` for a daemon the agent
     * registered at runtime through `reflex-service register`, absent (or a
     * plugin's own value) for a boot-time launch step. This is what lets the
     * host tell "a server the user should see listed" apart from setup
     * scaffolding when reconciling the persisted daemon list against the box.
     */
    source: z.string().optional(),
  })
  .meta({ id: 'AgentServiceStatus' });
export type AgentServiceStatus = z.infer<typeof AgentServiceStatusSchema>;

export const AgentServiceStatusesResponseSchema = z
  .object({
    services: z.array(AgentServiceStatusSchema),
  })
  .meta({ id: 'AgentServiceStatusesResponse' });
export type AgentServiceStatusesResponse = z.infer<typeof AgentServiceStatusesResponseSchema>;

export const AgentDaemonSchema = z.object({
  /** Display label the daemon registered with (e.g. "Storybook"). */
  name: z.string(),
  /** Port the daemon listens on inside the sandbox, when it exposed one. */
  port: z.number().int().positive().optional(),
  /** Pre-built tunnel URL (server derives it from `agent.tunnelKey` + port). */
  url: z.string().optional(),
  /** Short free-form status line supplied at registration. */
  info: z.string().optional(),
  /** Epoch-ms when the daemon last reported itself started. */
  startedAt: z.number(),
});
export type AgentDaemon = z.infer<typeof AgentDaemonSchema>;

/**
 * What a given caller may do with a session: they own it, an `edit` share
 * gave them write access, or they can only read it.
 *
 * The two share levels are deliberately the same vocabulary as
 * `ResourceGrantAccess` (`view` / `edit`), with `owner` on top — a grant
 * cannot express anything finer, because every mutating action resolves to
 * the one `agents:write` slug.
 */
export const AgentViewerAccessSchema = z
  .enum(['owner', 'edit', 'view'])
  .meta({ id: 'AgentViewerAccess' });
export type AgentViewerAccess = z.infer<typeof AgentViewerAccessSchema>;

/** Whether this access level permits mutating the session. */
export function canWriteAgent(access: AgentViewerAccess | undefined): boolean {
  // Absent means "the payload had no caller to answer for" — fail open and
  // let the server be the enforcement point. See `AgentSchema.viewerAccess`.
  return access !== 'view';
}

export const AgentSchema = z
  .object({
    id: z.string(),
    streamId: z.string(),
    agentType: AgentTypeSchema,
    status: AgentStatusSchema,
    /** Latest accepted ordinary-turn boundary; null until one is established. */
    turnState: AgentTurnStateSchema.nullable(),
    /** Database-owned monotonic ordering token for turn-state writes. */
    turnStateVersion: z.number().int().nonnegative(),
    devboxId: z.string().nullable(),
    tunnelKey: z.string().nullable().optional(),
    name: z.string(),
    prompt: z.string(),
    systemPrompt: z.string().nullable().optional(),
    /**
     * `prs_*` id of the persona this run was launched from, or null for a
     * hand-configured launch. A launch marker like {@link originator}: it
     * records where the config came from and stays set even if the persona
     * is later edited or deleted. Id only — surfaces resolve the persona's
     * current name/details live and fall back to the raw id when it no
     * longer resolves.
     */
    personaId: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    blueprintId: z.string().nullable().optional(),
    blueprintName: z.string().nullable().optional(),
    /**
     * The effective sandbox options the agent was launched with (devbox size,
     * blueprint/snapshot, dockerd, idle auto-suspend, ...). Resolved at launch
     * by the agent runner and persisted so downstream surfaces — notably the
     * "Save as persona" dialog — can carry the whole sandbox config forward
     * wholesale instead of cherry-picking individual fields. Null on legacy
     * rows created before this was captured.
     */
    sandboxOptions: SandboxOptionsSchema.nullable().optional(),
    promptMode: PromptModeSchema.nullable().optional(),
    createdAt: z.number(),
    /**
     * Epoch-ms of the agent's last activity (any stream event, user message,
     * or lifecycle change). The sandbox retention loop measures the
     * inactivity cleanup window from here. Backfilled from {@link createdAt}
     * for rows created before this was tracked.
     */
    lastActiveAt: z.number().nullable().optional(),
    archived: z.boolean(),
    /**
     * The principal the run is fenced by: a `usr_*` for a personal run, or
     * the `org_*` for one every member of the organization can reach. The
     * `agent` resource kind declares exactly those two scopes — there is no
     * "owned by nobody" shape.
     */
    ownerId: z.string(),
    /**
     * The run's owning user, or `null` when the organization owns it. A
     * projection of {@link ownerId}, kept because most surfaces ask "is this
     * someone's personal run?" rather than "which principal fences it?".
     */
    userId: z.string().nullable().optional(),
    /**
     * @deprecated Compatibility mirror of the most recently registered
     * `creator` pull-request association. New code should read
     * {@link associations} (via `agentPullRequests`) — an agent can be linked
     * to any number of PRs and issues, not just one.
     */
    prUrl: z.string().nullable().optional(),
    /**
     * Typed links to external work items (pull requests, Linear / Jira
     * issues) with the agent's role on each (`creator` / `reviewer` /
     * `assignee`). Replaces the single {@link prUrl}. Hydrated from the
     * `agent_associations` table by the server's query service; null on
     * payloads produced by serializers that do not hydrate it.
     */
    associations: z.array(AgentAssociationSchema).nullable().optional(),
    /**
     * Servers currently registered on the agent's sandbox. Upserted when the
     * on-box CLI reports a daemon start, cleared when the sandbox goes away.
     * Null/empty means nothing is being hosted right now.
     */
    daemons: z.array(AgentDaemonSchema).nullable().optional(),
    /**
     * Whether the broker is holding the devbox awake for background work.
     * Cleared on suspend, shutdown,
     * or permanent termination; independent of the ordinary turn state.
     */
    hasBackgroundWork: z.boolean().optional(),
    /** Latest broker membership snapshot, independent of transcript pagination. */
    backgroundTasks: z.array(BackgroundTaskSchema).nullable().optional(),
    /**
     * Whether this agent may arm listeners (timers, ref signals, watches)
     * for itself. Set from the launch dialog; turning it off cancels every
     * armed listener. Absent on rows older than the column, which read as
     * enabled.
     */
    listenersEnabled: z.boolean().optional(),
    /** The agent asked to suspend its devbox once the current turn ends. */
    suspendAtTurnEnd: z.boolean().optional(),
    attachments: z.array(PluginAttachmentValueSchema).nullable().optional(),
    envVars: z.array(AgentEnvVarSchema).nullable().optional(),
    organizationId: z.string(),
    /**
     * `mps_*` id of the model-provider secret resolved at agent launch
     * for the primary provider (inferred from {@link model}). Plugins
     * downstream (notably plugin-costs) use this to attribute spend or
     * activity to the exact key picked at start time, rather than a
     * post-hoc re-resolve that may land on a different tier.
     */
    modelProviderSecretId: z.string().nullable().optional(),
    /**
     * True when the agent's subscription was switched (see
     * `PUT /agents/:id/subscription`) while its devbox was not running, so the
     * new credential still has to be written onto the box. Consumed on the next
     * resume — the runner rewrites the on-box credential file and clears the
     * flag before dispatching the turn. Lets the UI show a "subscription will
     * apply on next turn" hint. Absent/false means the on-box credential already
     * matches {@link modelProviderSecretId}.
     */
    credentialSyncPending: z.boolean().nullable().optional(),
    /**
     * Id of the entity that launched this agent — the flow run, dashboard
     * user, API key, or integration run (Sentry/Linear/Jira/Slack) that
     * started it. The id itself is the discriminator: its 3-letter prefix
     * identifies the originator kind ({@link originatorPrefix}), except flow
     * runs, which mint bare UUIDs and so carry no prefix ({@link
     * isFlowOriginator}). Null for legacy rows created before originators were
     * tracked; treated as a plain agent. This is the single launch marker —
     * it replaced the former `flowRunId` column, so flow-launched agents hold
     * their run id here.
     */
    originator: z.string().nullable().optional(),
    pinned: z.boolean(),
    /**
     * The triage label a user applied to this run ({@link AgentMarkSchema}), or
     * null when the run carries none. Agent lists render it in place of the
     * derived activity icon, so a marked run reads as "a person has classified
     * this" at a glance. Cleared by the server on the next turn start or on
     * archive.
     */
    mark: AgentMarkSchema.nullable().optional(),
    /**
     * What the CALLER may do with this session. Computed per caller by the
     * agent read routes, exactly like `canManage` on a resource grant —
     * the same three levels the grant model supports, no finer, because
     * every mutating action collapses to `agents:write` (see
     * `agentGrantSlug`).
     *
     * The server enforces access regardless; this exists so the client can
     * stop rendering controls that are guaranteed to 403. Treat an absent
     * value as "unknown, do not gate" and fail OPEN: it is absent on every
     * payload with no single caller to answer for — the websocket
     * broadcasts, which fan one object out to the whole org, and the
     * plugin/lifecycle surfaces. Failing closed there would strip a user's
     * own brand-new session of its controls.
     */
    viewerAccess: AgentViewerAccessSchema.optional(),
  })
  .meta({ id: 'Agent' });
export type Agent = z.infer<typeof AgentSchema>;

/**
 * One row of the signed-in user's per-agent "last looked at" state. `unseen`
 * (whether an agent has activity newer than the viewer's last look) is derived
 * on the client by comparing this against the agent's `lastActiveAt`, so the
 * value can react to live websocket activity without a round trip.
 */
export const AgentViewSchema = z
  .object({
    agentId: z.string(),
    /** Epoch-ms the current user last opened this agent. */
    lastViewedAt: z.number(),
  })
  .meta({ id: 'AgentView' });
export type AgentView = z.infer<typeof AgentViewSchema>;

/** Response of `GET /agents/views` — every agent the caller has ever opened. */
export const AgentViewsResponseSchema = z
  .object({
    views: z.array(AgentViewSchema),
  })
  .meta({ id: 'AgentViewsResponse' });
export type AgentViewsResponse = z.infer<typeof AgentViewsResponseSchema>;

/** The built-in originator kind for user-, dashboard-, or agent-started runs. */
export const ORIGINATOR_KIND_AGENT = 'agent';
/** The built-in originator kind for a flow-launched run. */
export const ORIGINATOR_KIND_FLOW = 'flow';

/**
 * The 3-letter id prefix of an originator id (e.g. `usr`, `srn`), or null when
 * the id carries no prefix. A missing prefix means a flow run — flow runs mint
 * bare UUIDs, the one source that doesn't stamp a prefixed id.
 */
export function originatorPrefix(id: string | null | undefined): string | null {
  if (!id) return null;
  const match = /^([a-z]{3})_/.exec(id);
  return match ? match[1] : null;
}

/**
 * Whether an originator id identifies a flow run. Flow runs are the only
 * launch source that stamps a bare (unprefixed) UUID, so a present-but-
 * unprefixed originator is a flow launch. Used by surfaces that treat flow
 * runs specially (e.g. Slack skips per-agent notifications for them).
 */
export function isFlowOriginator(originator: string | null | undefined): boolean {
  return !!originator && originatorPrefix(originator) === null;
}

/**
 * Whether an agent's `originator` belongs to the given originator kind. Kinds
 * are keyed by id-prefix, with two built-ins that aren't prefix lookups:
 *   - {@link ORIGINATOR_KIND_AGENT}: plain agents — a null originator (legacy),
 *     a `usr_` (user/dashboard) launch, or an `agt_` (subagent) launch. The
 *     default bucket keeps delegated children visible in the normal run list.
 *   - {@link ORIGINATOR_KIND_FLOW}: flow runs — a bare, unprefixed originator.
 * Any other kind key is matched literally against the originator's prefix, so
 * plugin-contributed kinds (Sentry `srn`, Linear `lnr`, …) just use their
 * prefix as the kind key.
 */
export function matchesOriginatorKind(
  originator: string | null | undefined,
  kind: string,
): boolean {
  if (kind === ORIGINATOR_KIND_AGENT) {
    const prefix = originatorPrefix(originator);
    return originator == null || originator === '' || prefix === 'usr' || prefix === 'agt';
  }
  if (kind === ORIGINATOR_KIND_FLOW) return isFlowOriginator(originator);
  return originatorPrefix(originator) === kind;
}

export const AgentConfigSchema = z.object({
  name: z.string().optional(),
  agentType: AgentTypeSchema,
  prompt: z.string().min(1),
  systemPrompt: z.string().optional(),
  /**
   * `prs_*` id of the persona this launch was configured from, recorded on
   * the run for provenance (see `Agent.personaId`). Display metadata only —
   * the persona's config is already flattened into the other fields by the
   * launcher (server-side for `/agent-personas/:id/launch`, client-side in
   * the launch dialog), so the server never re-reads the persona from this.
   */
  personaId: z.string().max(64).optional(),
  model: z.string().optional(),
  modelEndpointId: z.string().min(1).optional(),
  /** @deprecated Use `sandboxOptions.blueprintId`. Kept for backwards compatibility with older clients. */
  blueprintId: z.string().optional(),
  /** @deprecated Use `sandboxOptions.blueprintName`. Kept for backwards compatibility with older clients. */
  blueprintName: z.string().optional(),
  /**
   * Generic options for the sandbox the agent runs in (blueprint, devbox size, ...).
   * Promoted by the active {@link SandboxProvider}. When set, takes precedence over
   * the deprecated top-level `blueprintId`/`blueprintName` fields.
   */
  sandboxOptions: SandboxOptionsSchema.optional(),
  content: UserContentBlocksSchema.optional(),
  attachments: z.array(PluginAttachmentValueSchema).optional(),
  envVars: z.array(AgentEnvVarSchema).optional(),
  promptMode: PromptModeSchema.optional(),
  /**
   * Whether the agent may arm listeners for itself. Defaults on; the launch
   * dialog's "Allow listeners" toggle sends false to opt a run out.
   */
  listenersEnabled: z.boolean().optional(),
  /**
   * Labels (`alb_*`) to put on the run at launch, from the organization's
   * catalog. Filing a run is otherwise something you do to it after the fact,
   * which means the runs that most want a label — the ones you launch and
   * walk away from — are the ones that never get one. Ids only: a name that
   * isn't in the catalog yet is created through `POST /agent-labels` before
   * the launch, so there is one place a label can be minted.
   *
   * An id that no longer resolves is skipped rather than failing the launch
   * (see `AgentRunnerService.applyLaunchTaxonomy`) — a persona or flow node
   * can outlive the label it was saved with.
   */
  labelIds: z.array(coreIds.schema('agentLabel')).max(MAX_LAUNCH_LABELS).optional(),
  /**
   * Group (`agp_*`) to file the run into at launch. Singular because that is
   * the gesture: a run belongs to the piece of work you launched it for. Add
   * it to further groups afterwards through the agent list, which is where
   * multi-group membership is actually useful.
   */
  groupId: coreIds.schema('agentGroup').optional(),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

export const AgentConfigInputSchema = AgentConfigSchema.extend({
  envVars: z.array(AgentEnvVarInputSchema).optional(),
  /**
   * Authentication method for agent launches. Defaults to 'org-key' which
   * uses the organization's provider API key. Personal subscription methods
   * mount the user's CLI auth file into the devbox — no org API key is used.
   *
   * @see docs/internal/product/claude-max.md
   */
  authMethod: AgentAuthMethodSchema.optional(),
  /**
   * `mps_*` id of the model-provider secret the launcher explicitly
   * picked for the primary provider. When set, the agent runner uses
   * this exact key (apiKey or subscription) instead of walking the
   * user -> team -> org precedence, and derives {@link authMethod}
   * from the resolved row's type. The runner validates the launching
   * user can actually see the secret's tier before using it.
   */
  providerSecretId: ModelProviderSecretIdSchema.optional(),
});
export type AgentConfigInput = z.infer<typeof AgentConfigInputSchema>;

// ── Git repo attachment helpers ───────────────────────────────────
//
// plugin-github owns the `git-repo` attachment. These helpers live in
// `@reflex/shared` so every consumer (server, plugins, web) can
// read the repo slug/branch from an agent's attachments without
// depending on plugin-github directly.

/**
 * Extract the `{ repoSlug, repoBranch }` carried by an agent's git-repo
 * attachment, if any. Returns `undefined` when the attachment is missing
 * or malformed — callers should treat that as "no repo configured".
 */
export function getGitRepoAttachment(agent: {
  attachments?: PluginAttachmentValue[] | null;
}): GitRepoAttachmentConfig | undefined {
  const entry = agent.attachments?.find((a) => a.attachmentId === GIT_REPO_ATTACHMENT_ID);
  if (!entry) return undefined;
  const parsed = GitRepoAttachmentConfigSchema.safeParse(entry.config);
  return parsed.success ? parsed.data : undefined;
}

export const AgentActivitySchema = z.enum([
  'starting',
  'working',
  'waiting_input',
  'idle',
  'interrupted',
  'completed',
  'error',
  'stopped',
]);
export type AgentActivity = z.infer<typeof AgentActivitySchema>;

export const ACTIVE_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'running',
  'starting',
  'needs_input',
]);

export const NON_TERMINAL_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'running',
  'starting',
  'needs_input',
  'interrupted',
]);

export const TERMINAL_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'stopped',
  'error',
  'completed',
  'terminated',
]);

/**
 * Terminal statuses whose devbox and Axon stream are gone for good.
 * `completed` is deliberately excluded: it is terminal for the lifecycle but
 * resumable for the product — the sandbox and stream are retained so the
 * conversation continues on the next message. Drives stream-release and
 * devbox-retention decisions; guards about "is the agent finished" use
 * {@link TERMINAL_STATUSES} instead.
 */
export const NON_RESUMABLE_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'stopped',
  'error',
  'terminated',
]);

export const INTERRUPTABLE_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'starting',
  'running',
  'stopping',
]);

export const ATTENTION_STATUSES: ReadonlySet<AgentStatus> = new Set(['needs_input', 'interrupted']);

/** Statuses that can still take a future turn. */
export const TURN_CAPABLE_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'starting',
  'running',
  'needs_input',
  'interrupted',
  'completed',
]);

/**
 * Statuses from which a run's model may be switched. Not `starting`: the
 * launch sends its own model as its last step and could overwrite a switch.
 */
export const MODEL_SWITCHABLE_STATUSES: ReadonlySet<AgentStatus> = new Set([
  'running',
  'needs_input',
  'interrupted',
  'completed',
]);
